import { faCheckCircle, faGear } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition } from '@headlessui/react'
import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { useQuotation } from '../context'

import { OnboardingTitle } from './OnboardingTitle'
import { FormattedPrice } from './FormattedPrice'

import { Button, Card, CardContent, Select, Badge } from '@olino/design-system'

const guaranteeAmounts = [15_000, 25_000, 50_000, 75_000]

const title = 'Tous Dommages Matériels Informatiques (TDMI)'
const description =
  'Protégez votre matériel informatique sur votre lieu de travail (vos locaux, en déplacement chez vos clients, etc.)'

export const PricingEcommAllComputerMaterialDamageOption: React.FC = () => {
  const { quotation, updateQuotation } = useQuotation()

  const [configDialogOpen, setConfigDialogOpen] = useState(false)

  const details = quotation.details.publicLiabilityEcomm
  const premium = quotation.premium?.publicLiabilityEcomm

  const selected = !!details?.allComputerMaterialDamageOption

  const price = details?.allComputerMaterialDamageOption && premium?.allComputerMaterialDamageOption

  const onClick = () => {
    if (!details?.allComputerMaterialDamageOption) setConfigDialogOpen(true)

    updateQuotation({
      details: {
        publicLiabilityEcomm: {
          allComputerMaterialDamageOption: !details?.allComputerMaterialDamageOption,
        },
      },
    })
  }

  const onConfigClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setConfigDialogOpen(true)
  }

  return (
    <>
      <div className="flex flex-row items-stretch">
        <button type="button" role="switch" aria-checked={selected ?? false} onClick={onClick}>
          <Card
            className={twMerge(
              'cursor-pointer overflow-hidden transition hover:border-primary-900',
              selected && 'border-primary-900 shadow-clone-xl'
            )}
          >
            <CardContent className="flex flex-row items-center">
              <div
                className={twMerge(
                  'mr-3 flex h-7 w-7 items-center justify-center rounded-full border border-grey-300 sm:mr-5'
                )}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className={twMerge(
                    'h-7 w-7 text-primary-600',
                    'transition-transform ease-in-out',
                    selected ? 'rotate-0 scale-100' : 'rotate-90 scale-0'
                  )}
                />
              </div>

              <div className="flex flex-grow flex-col sm:flex-row sm:items-center">
                <div className="flex-grow">
                  <div className="flex flex-row flex-wrap items-center">
                    <OnboardingTitle.H3Pricing className="font-bold">
                      {title}
                    </OnboardingTitle.H3Pricing>
                    <Badge size="small" className="mr-2">
                      Recommandé
                    </Badge>
                  </div>
                  <p className="hidden text-left text-sm sm:block">{description}</p>
                </div>

                <p className="my-1 text-left text-sm font-light sm:hidden">{description}</p>

                {price ? <FormattedPrice size="sm" originalAmount={price} hideLoader /> : null}
              </div>
            </CardContent>
          </Card>
        </button>

        <button
          className={twMerge(
            'flex flex-shrink-0 items-center justify-center rounded-lg border bg-grey-100 transition-all hover:bg-grey-200',
            selected
              ? 'ml-4 w-10 opacity-100 sm:w-14'
              : 'pointer-events-none w-0 border-none opacity-0'
          )}
          type="button"
          onClick={onConfigClick}
        >
          <FontAwesomeIcon icon={faGear} className="w-full text-grey-400" />
        </button>
      </div>

      <Transition appear show={configDialogOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setConfigDialogOpen(false)}>
          {/* Backdrop */}
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-info-200 bg-opacity-25 backdrop-blur" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="max-w-2xl">
                  <Card>
                    <CardContent className="px-11 py-6">
                      <Dialog.Title as="h3" className="mb-2 text-2xl font-bold text-primary-900">
                        {title}
                      </Dialog.Title>

                      <p>
                        L'option TDMI offre une protection complète à votre matériel informatique
                        professionnel (ordinateur, tablette, téléphone, etc.) et à votre mobilier
                        professionnel.
                      </p>
                      <p>
                        Que ce soit chez vous, au travail ou en déplacement, vous pouvez avoir
                        l'esprit tranquille en sachant que votre matériel est couvert contre:
                      </p>

                      <ul className="list-disc pl-6 text-sm">
                        <li>
                          la perte, la casse, le vol (y compris en véhicules) et le vandalisme
                        </li>
                        <li>Incendie, dommages électriques, explosion, foudre</li>
                        <li>Dégât des eaux</li>
                        <li>Catastrophes naturelles</li>
                        <li>Attentats et actes de terrorisme</li>
                        <li>Catastrophes technologiques</li>
                      </ul>

                      <h4 className="mt-3 font-bold">Exemple</h4>
                      <p className="text-sm">
                        Votre ordinateur est volé dans votre véhicule professionnel, l'assureur
                        prend charge l'indemnisation du remplacement de celui-ci.
                      </p>

                      <h4 className="mt-3 text-base font-bold">Montant maximal des garanties</h4>
                      <p>
                        Il s'agit de la somme maximale pour laquelle vous pouvez être indemnisé.
                      </p>
                      <Select
                        options={guaranteeAmounts}
                        value={details?.allComputerMaterialDamageGuaranteeAmount}
                        onChange={(value) =>
                          updateQuotation({
                            details: {
                              publicLiabilityEcomm: {
                                allComputerMaterialDamageGuaranteeAmount: value,
                              },
                            },
                          })
                        }
                      />

                      <div className="mt-4">
                        <Button
                          className="mx-auto"
                          size="sm"
                          onClick={() => setConfigDialogOpen(false)}
                        >
                          Confirmer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
